import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import { routeV1 } from "./route.v1";
import PageTitleUpdater from "../../router/page-router-updater";

// 홈(랜딩·LCP 타깃)은 eager import — 진입 시 추가 청크 라운드트립 방지.
import HomeRenewal from "./home/HomeRenewal";

// 그 외 라우트는 모두 코드 분리(lazy) — 홈만 방문할 때 다른 페이지 코드를
// 초기 번들에 포함시키지 않아 main.js 다운로드/파싱 비용을 줄인다.
const ProductsRenewal = lazy(() => import("./products/ProductsRenewal"));
const ServicePage = lazy(() => import("./services/DataPage"));
const IntroduceRenewal = lazy(() => import("./introduce/overview/IntroduceRenewal"));
const History = lazy(() => import("./introduce/history/history"));
const MajorPerformanceRenewal = lazy(() => import("./introduce/heritage/MajorPerformanceRenewal"));
const Workspace = lazy(() => import("./introduce/workspace/Workspace"));
const Direction = lazy(() => import("./introduce/direction/direction"));

const ElectronicAnnounce = lazy(() => import("./investment/electronic-announcement/electronicAnnounce"));
const IrInfoPage = lazy(() => import("./investment/ir-info/page"));
const IrInfoDetailPage = lazy(() => import("./investment/ir-info/detail/page"));
const ManagementInfo = lazy(() => import("./investment/management-info/managementInfo"));
const ManagementInfoDetail = lazy(() => import("./investment/management-info/detail/managementInfoDetail"));

const NewArticle = lazy(() => import("./cyber-relation/new-article/newArticle"));
const LumirNews = lazy(() => import("./cyber-relation/lumir-news/lumirNews"));
const LumirNewsDetail = lazy(() => import("./cyber-relation/lumir-news/detail/lumirNewsDetail"));
const Brochure = lazy(() => import("./cyber-relation/public-relations-office/brochure/brochure"));
const VideoGallery = lazy(() => import("./cyber-relation/public-relations-office/video-gallery/videoGallery"));

const Apply = lazy(() => import("./recruit/apply/apply"));
const ApplySuccess = lazy(() => import("./recruit/apply/success/applySuccess"));
const ApplyFailed = lazy(() => import("./recruit/apply/failed/applyFailed"));
const Talent = lazy(() => import("./recruit/talent-type/Talent"));
const NewJobAnnounce = lazy(() => import("./recruit/job-announce/newJobAnnounce"));
const NewJobAnnounceDetail = lazy(() => import("./recruit/job-announce/detail/newJobAnnounceDetail"));
const RecruitMaintenance = lazy(() => import("./recruit/_component/RecruitMaintenance"));

const isRecruitMaintenance = process.env.REACT_APP_RECRUIT_MAINTENANCE === "true";

const NotFoundPage = lazy(() => import("../NotFoundPage"));

// 메뉴 hover 시 해당 라우트의 JS 청크를 미리 다운로드하기 위한 prefetch 맵.
// webpack은 동일한 import() 경로를 중복 청크 없이 하나로 관리하므로
// 아래 import() 호출은 lazy()와 동일한 청크를 재사용한다.
export const prefetchChunkMap = {
  [routeV1.products]:            () => import("./products/ProductsRenewal"),
  [routeV1.service]:             () => import("./services/DataPage"),
  [routeV1.overview]:            () => import("./introduce/overview/IntroduceRenewal"),
  [routeV1.introduce]:           () => import("./introduce/overview/IntroduceRenewal"),
  [routeV1.history]:             () => import("./introduce/history/history"),
  [routeV1.heritage]:            () => import("./introduce/heritage/MajorPerformanceRenewal"),
  [routeV1.workspace]:           () => import("./introduce/workspace/Workspace"),
  [routeV1.direction]:           () => import("./introduce/direction/direction"),
  [routeV1.electronicAnnounce]:  () => import("./investment/electronic-announcement/electronicAnnounce"),
  [routeV1.ir_info]:             () => import("./investment/ir-info/page"),
  [routeV1.managementInfo]:      () => import("./investment/management-info/managementInfo"),
  [routeV1.newArticle]:          () => import("./cyber-relation/new-article/newArticle"),
  [routeV1.lumirNews]:           () => import("./cyber-relation/lumir-news/lumirNews"),
  [routeV1.videoGallery]:        () => import("./cyber-relation/public-relations-office/video-gallery/videoGallery"),
  [routeV1.brochure]:            () => import("./cyber-relation/public-relations-office/brochure/brochure"),
  [routeV1.talentType]:          () => import("./recruit/talent-type/Talent"),
  [routeV1.jobAnnounce]:         () => import("./recruit/job-announce/newJobAnnounce"),
};

// lazy 청크 다운로드 동안 본문이 비어 0높이가 되면 Footer가 상단으로 튀어오른다.
// 뷰포트 높이를 예약해 레이아웃을 고정(footer 점프 방지) + 로딩 상태 노출.
const RouteFallback = () => (
  <div style={{ minHeight: "100vh" }} aria-busy="true" aria-live="polite" />
);

const Router = () => {
  return (
    <>
      <PageTitleUpdater />
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* 메인 */}
          <Route path={routeV1.default} element={<HomeRenewal />} />

          {/* 제품 */}
          <Route path={routeV1.products} element={<ProductsRenewal />} />

          {/* 서비스 */}
          <Route path={routeV1.service} element={<ServicePage />} />

          {/* 회사소개 */}
          <Route path={routeV1.overview} element={<IntroduceRenewal />} />
          <Route path={routeV1.history} element={<History />} />
          <Route path={routeV1.heritage} element={<MajorPerformanceRenewal />} />
          <Route path={routeV1.workspace} element={<Workspace />} />
          <Route path={routeV1.direction} element={<Direction />} />

          {/* 투자 */}
          <Route
            path={routeV1.electronicAnnounce}
            element={<ElectronicAnnounce />}
          />
          <Route path={routeV1.ir_info + "/:page"} element={<IrInfoPage />} />
          <Route
            path={routeV1.ir_info_detail + "/:id"}
            element={<IrInfoDetailPage />}
          />
          <Route
            path={routeV1.managementInfo + "/:page"}
            element={<ManagementInfo />}
          />
          <Route
            path={routeV1.managementInfoDetail + "/:id"}
            element={<ManagementInfoDetail />}
          />

          {/* 정보 */}
          <Route path={routeV1.newArticle + "/:page"} element={<NewArticle />} />
          <Route path={routeV1.lumirNews + "/:page"} element={<LumirNews />} />
          <Route
            path={routeV1.lumirNewsDetail + "/:id"}
            element={<LumirNewsDetail />}
          />
          <Route path={routeV1.brochure + "/:page"} element={<Brochure />} />
          <Route path={routeV1.videoGallery} element={<VideoGallery />} />

          {/* 채용 */}
          <Route path={routeV1.apply + "/:id"} element={<Apply />} />
          <Route path={routeV1.applySuccess} element={<ApplySuccess />} />
          <Route path={routeV1.applyFailed} element={<ApplyFailed />} />

          <Route path={routeV1.talentType} element={<Talent />} />
          <Route
            path={routeV1.jobAnnounce + "/:page"}
            element={isRecruitMaintenance ? <RecruitMaintenance /> : <NewJobAnnounce />}
          />
          <Route
            path={routeV1.jobAnnounceDetail + "/:id"}
            element={isRecruitMaintenance ? <RecruitMaintenance /> : <NewJobAnnounceDetail />}
          />

          {/* 공통 */}
          <Route path={routeV1.notFound} element={<NotFoundPage />} />
          <Route path="*" element={<Navigate replace to={routeV1.notFound} />} />
        </Routes>
      </Suspense>
    </>
  );
};

export default Router;
